/**
 * Legacy `Guest.rsvpStatus` values mapped onto the `GuestRsvp` responses.
 * The `attending`/`not_attending` spellings are older portal wording that
 * still shows up in imported data.
 */
export const LEGACY_RSVP_STATUS_TO_RESPONSE = {
    confirmed: 'YES',
    attending: 'YES',
    declined: 'NO',
    not_attending: 'NO',
    maybe: 'MAYBE',
    invited: 'PENDING',
    pending: 'PENDING',
};
/** Human labels for a response, for tables and reports. */
export const RSVP_RESPONSE_LABEL = {
    YES: 'Yes',
    NO: 'No',
    MAYBE: 'Maybe',
    PENDING: 'Pending',
};
const num = (value) => typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
/**
 * Resolve how many people a guest is actually bringing.
 *
 * The RSVP row wins when there is one; otherwise the party composition on the
 * guest row stands in, so a guest confirmed by hand still reports a head count.
 * Only an attending guest contributes people — a declined party of six is zero
 * chairs, not six.
 */
export function resolveGuestAttendance(input) {
    const response = input.rsvp?.response ??
        LEGACY_RSVP_STATUS_TO_RESPONSE[(input.rsvpStatus ?? '').toLowerCase()] ??
        'PENDING';
    const attending = response === 'YES';
    const adults = input.rsvp
        ? num(input.rsvp.adultCount)
        : num(input.maleCount) + num(input.femaleCount);
    const kids = input.rsvp ? num(input.rsvp.kidCount) : num(input.kidCount);
    return {
        response,
        attending,
        adults: attending ? adults : 0,
        kids: attending ? kids : 0,
        total: attending ? adults + kids : 0,
        invitedTotal: adults + kids,
    };
}
/** Roll a list of resolved attendances up into the numbers a summary shows. */
export function sumGuestAttendance(entries) {
    return entries.reduce((totals, entry) => {
        if (entry.response === 'YES')
            totals.yes += 1;
        else if (entry.response === 'NO')
            totals.no += 1;
        else if (entry.response === 'MAYBE')
            totals.maybe += 1;
        else
            totals.pending += 1;
        totals.adults += entry.adults;
        totals.kids += entry.kids;
        totals.attendants += entry.total;
        return totals;
    }, { yes: 0, no: 0, maybe: 0, pending: 0, adults: 0, kids: 0, attendants: 0 });
}
/**
 * "3 adults · 1 child" — the breakdown shown under a head count. Empty when
 * there is nobody to describe, so callers can fall back to a dash.
 */
export function formatAttendanceBreakdown(adults, kids) {
    const parts = [];
    if (adults > 0)
        parts.push(`${adults} adult${adults === 1 ? '' : 's'}`);
    if (kids > 0)
        parts.push(`${kids} ${kids === 1 ? 'child' : 'children'}`);
    return parts.join(' · ');
}
/**
 * Male/female counts for a new number of adults, keeping the split already
 * stored where it can. Screens that ask only "how many adults" (the client
 * portal, the public RSVP) must not wipe a producer's split: an unchanged
 * total keeps it exactly, extra adults are added to the male count, and fewer
 * are taken from the male count first, then the female.
 */
export function splitAdults(current, adults) {
    const target = Math.max(0, Math.floor(adults));
    const { maleCount, femaleCount } = current;
    const total = maleCount + femaleCount;
    if (target >= total)
        return { maleCount: maleCount + (target - total), femaleCount };
    const fromMale = Math.min(maleCount, total - target);
    return {
        maleCount: maleCount - fromMale,
        femaleCount: femaleCount - (total - target - fromMale),
    };
}
//# sourceMappingURL=attendance.js.map